import { Link } from '@inertiajs/react'

// The brand: the clipboard mark and the "FANTASY / Call Sheet" wordmark
// (docs/design.md "Brand"). Every layout's top-left link is `Brand`, so the
// name and the mark live here once.
//
// The mark is fixed artwork, so its fills are literal: the violet board, the
// white paper and the ember route stay the same in both themes. Only the outer
// outline is a runtime property (`--brand-outline`), ink on a light ground and
// the dark canvas on a dark one, where an ink outline would vanish; the X's,
// O's and routes inside sit on the white paper and keep the ink in both.

const INK = '#191436'

export function LogoMark({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 512 512" aria-hidden="true" focusable="false" className={className}>
      <g
        stroke="var(--brand-outline)"
        strokeLinecap="round"
        strokeLinejoin="round"
        transform="rotate(-6 256 256)"
      >
        <path d="M128 92 Q120 92 119 104 L114 432 Q114 448 130 448 L384 452 Q398 452 398 438 L400 104 Q400 90 386 90 Z" fill="#5340d4" strokeWidth="14" />
        <path d="M146 124 L370 120 Q376 120 376 128 L374 420 Q374 426 366 426 L148 428 Q140 428 140 420 L140 132 Q140 124 146 124 Z" fill="#ffffff" strokeWidth="10" />
        <path d="M206 66 Q206 54 220 54 L292 54 Q306 54 306 66 L310 112 Q310 122 298 122 L214 122 Q202 122 202 112 Z" fill="#e4e0f4" strokeWidth="12" />
        <circle cx="256" cy="80" r="10" fill={INK} stroke="none" />
        <path d="M170 166 q10 -9 20 -1 t20 0 t20 -1 t20 1 t20 -1 t20 1 t20 0 t20 -1 t18 2" fill="none" stroke="#585379" strokeWidth="9" />
        <path d="M170 198 q10 -8 20 0 t20 -1 t20 1 t20 0 t18 -1" fill="none" stroke="#585379" strokeWidth="9" />
        <path d="M182 240 l20 20 M202 240 l-20 20" stroke={INK} strokeWidth="9" />
        <path d="M314 250 l20 20 M334 250 l-20 20" stroke={INK} strokeWidth="9" />
        <path d="M164 306 L352 304" stroke="#847ea6" strokeWidth="6" strokeDasharray="4 14" />
        <circle cx="196" cy="340" r="15" fill="#ffffff" stroke={INK} strokeWidth="8" />
        <circle cx="256" cy="342" r="15" fill="#ffffff" stroke={INK} strokeWidth="8" />
        <circle cx="316" cy="340" r="15" fill="#ffffff" stroke={INK} strokeWidth="8" />
        <circle cx="256" cy="392" r="15" fill="#ffffff" stroke={INK} strokeWidth="8" />
        <path d="M196 322 Q196 294 168 290" fill="none" stroke={INK} strokeWidth="7" />
        <path d="M164 302 L164 288 L178 286" fill="none" stroke={INK} strokeWidth="7" />
        <path d="M256 322 L258 268 Q262 236 300 222 L338 206" fill="none" stroke="#ef5f10" strokeWidth="12" />
        <path d="M318 196 L342 204 L330 226" fill="none" stroke="#ef5f10" strokeWidth="12" />
      </g>
    </svg>
  )
}

// "Call Sheet" in the marker face, "Sheet" in ember. A logotype, so the ember
// word is exempt from the 4.5:1 text rule (WCAG 1.4.3); it is never used for
// running text.
export function Wordmark({ className }: { className: string }) {
  return (
    <span className={'font-marker whitespace-nowrap ' + className}>
      Call <span className="text-highlight">Sheet</span>
    </span>
  )
}

// The mark and the two-line wordmark as the link home. `header` is the app
// shell's size (38px mark on a phone, 42px from md); `large` is the landing
// page's and the account card's (44px). The visible text is two stacked
// spans, so the link's name comes from the sr-only line instead.
export default function Brand({ size = 'header' }: { size?: 'header' | 'large' }) {
  const large = size === 'large'
  return (
    <Link
      href="/"
      className="inline-flex min-h-touch items-center gap-2 rounded-control text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-accent md:gap-2.5"
    >
      <LogoMark className={large ? 'h-11 w-11 shrink-0' : 'h-9.5 w-9.5 shrink-0 md:h-10.5 md:w-10.5'} />
      <span aria-hidden="true" className="grid leading-none">
        <span
          className={
            'font-display font-bold tracking-[0.28em] text-accent uppercase ' + (large ? 'text-[11px]' : 'text-[9px] md:text-[10px]')
          }
        >
          Fantasy
        </span>
        <Wordmark className={large ? 'text-[26px]' : 'text-[21px] md:text-2xl'} />
      </span>
      <span className="sr-only">Fantasy Call Sheet</span>
    </Link>
  )
}

// The marketing type and calls to action (docs/design.md "Brand"), shared so
// a new signed-out surface reads like the landing page. The two CTAs are
// classes, not a component: each is a `Link` (it navigates), and a page adds
// size or a glow (`min-h-13 px-6 text-lg shadow-ember` for the hero's).
export const CTA_EMBER =
  'inline-flex min-h-touch items-center justify-center rounded-card bg-highlight px-5 whitespace-nowrap font-display font-bold text-highlight-ink transition hover:brightness-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2'
export const CTA_GHOST =
  'inline-flex min-h-touch items-center justify-center rounded-card px-4 whitespace-nowrap font-display font-semibold text-ink transition-colors hover:bg-accent-weak focus:outline-none focus-visible:ring-2 focus-visible:ring-accent'
// The uppercase label over a section heading; the page adds its colour.
export const EYEBROW = 'font-display text-sm font-bold tracking-[0.24em] uppercase'
// A section heading on a marketing page: 34 px at the floor, 48 px on desktop.
export const SECTION_HEADING = 'font-display text-[clamp(2.125rem,4vw,3rem)] leading-[1.05] font-extrabold text-balance'
